/**
 * Mathematical utility functions for geometric calculations
 */
import { MATH_CONSTANTS } from './constants.js';

export const MathUtils = {
    /**
     * Convert degrees to radians
     * @param {number} degrees - Angle in degrees
     * @returns {number} Angle in radians
     */
    degreesToRadians(degrees) {
        return degrees * MATH_CONSTANTS.DEGREES_TO_RADIANS;
    },

    /**
     * Convert radians to degrees
     * @param {number} radians - Angle in radians
     * @returns {number} Angle in degrees
     */
    radiansToDegrees(radians) {
        return radians * MATH_CONSTANTS.RADIANS_TO_DEGREES;
    },

    /**
     * Calculate distance between two points
     * @param {Object} point1 - First point {x, y}
     * @param {Object} point2 - Second point {x, y}
     * @returns {number} Distance between points
     */
    distance(point1, point2) {
        const dx = point2.x - point1.x;
        const dy = point2.y - point1.y;
        return Math.sqrt(dx * dx + dy * dy);
    },

    /**
     * Rotate a point around another point
     * @param {Object} point - Point to rotate {x, y}
     * @param {number} angle - Rotation angle in radians
     * @param {Object} origin - Center of rotation {x, y}
     * @returns {Object} Rotated point {x, y}
     */
    rotatePoint(point, angle, origin = { x: 0, y: 0 }) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);

        const dx = point.x - origin.x;
        const dy = point.y - origin.y;

        return {
            x: origin.x + (dx * cos - dy * sin),
            y: origin.y + (dx * sin + dy * cos),
        };
    },

    /**
     * Check if point is inside rectangle
     * @param {Object} point - Point to check {x, y}
     * @param {Object} rect - Rectangle {x, y, width, height}
     * @returns {boolean} True if point is inside rectangle
     */
    pointInRect(point, rect) {
        return (
            point.x >= rect.x &&
            point.x <= rect.x + rect.width &&
            point.y >= rect.y &&
            point.y <= rect.y + rect.height
        );
    },

    /**
     * Clamp a value between min and max
     * @param {number} value - Value to clamp
     * @param {number} min - Minimum value
     * @param {number} max - Maximum value
     * @returns {number} Clamped value
     */
    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    },
};

/**
 * Square class representing a square in the fractal
 */
export class Square {
    /**
     * Create a square
     * @param {number} centerX - Center X coordinate
     * @param {number} centerY - Center Y coordinate
     * @param {number} size - Square side length
     * @param {number} rotationAngle - Rotation angle in radians
     * @param {number} level - Recursion level (0 = base)
     */
    constructor(centerX, centerY, size, rotationAngle, level) {
        this.x = centerX;
        this.y = centerY;
        this.size = size;
        this.angle = rotationAngle;
        this.level = level;

        // Calculate corners from center
        this.corners = this.calculateCorners();
    }

    /**
     * Calculate corner coordinates
     * @returns {Array} Array of corner points {x, y}
     */
    calculateCorners() {
        const halfSize = this.size / 2;

        // Define corners relative to center (clockwise from top-left)
        // This order is CRITICAL for triangle construction
        const corners = [
            { x: -halfSize, y: -halfSize }, // Top-left (corners[0])
            { x: halfSize, y: -halfSize }, // Top-right (corners[1])
            { x: halfSize, y: halfSize }, // Bottom-right (corners[2])
            { x: -halfSize, y: halfSize }, // Bottom-left (corners[3])
        ];

        // Rotate each corner around center and translate to world position
        return corners.map(corner => {
            const rotated = MathUtils.rotatePoint(corner, this.angle, { x: 0, y: 0 });
            return {
                x: rotated.x + this.x,
                y: rotated.y + this.y,
            };
        });
    }

    /**
     * Get the top edge of the square for triangle construction
     * @returns {Object} Line segment {start, end}
     */
    getTopEdge() {
        return {
            start: this.corners[0], // Top-left
            end: this.corners[1], // Top-right
        };
    }
}

/**
 * Triangle class representing a triangle in the fractal construction
 */
export class Triangle {
    /**
     * Create a triangle
     * @param {Object} point1 - First vertex {x, y}
     * @param {Object} point2 - Second vertex {x, y}
     * @param {Object} point3 - Third vertex {x, y}
     * @param {number} level - Recursion level
     */
    constructor(point1, point2, point3, level) {
        this.vertices = [point1, point2, point3];
        this.level = level;
    }

    /**
     * Get the two sides of the triangle (excluding the base)
     * @returns {Array} Array of two line segments
     */
    getSides() {
        const [base1, base2, apex] = this.vertices;

        return [
            {
                // Left side
                start: base1,
                end: apex,
                length: MathUtils.distance(base1, apex),
            },
            {
                // Right side
                start: apex,
                end: base2,
                length: MathUtils.distance(apex, base2),
            },
        ];
    }
}
